import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { shareAPI } from '../lib/api';
import { getInitials } from '../lib/utils';
import { Calendar, Tag, Users, Clock, AlertTriangle, MessageSquare, History, Loader2 } from 'lucide-react';

const STATUS_COLORS = {
  backlog: '#6b7280', todo: '#3b82f6', in_progress: '#f59e0b', ready_for_testing: '#8b5cf6',
  done: '#10b981', released: '#22c55e', cancelled: '#ef4444',
};
const STATUS_LABELS = {
  backlog: 'Backlog', todo: 'To Do', in_progress: 'In Progress', ready_for_testing: 'Ready for Testing',
  done: 'Done', released: 'Released', cancelled: 'Cancelled',
};
const PRIORITY_COLORS = { high: '#ef4444', urgent: '#ef4444', medium: '#f59e0b', low: '#3b82f6', none: '#94a3b8' };

export default function SharedTaskView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('details');

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await shareAPI.getShared(token);
        setData(res.data);
      } catch {
        setError('This share link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Link Not Found</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const { task, comments, history } = data;
  const sColor = STATUS_COLORS[task.status] || '#6b7280';
  const pColor = PRIORITY_COLORS[task.priority] || '#94a3b8';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-task-view">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{task.task_number}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: sColor }}>
                {STATUS_LABELS[task.status] || task.status}
              </span>
              <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ color: pColor, background: `${pColor}15` }}>
                {(task.priority || 'none').toUpperCase()}
              </span>
            </div>
            <h1 className="text-xl font-bold text-slate-800">{task.title}</h1>
            {task.client?.name && (
              <p className="text-sm text-slate-500 mt-1">Project: {task.client.name}</p>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-4 sm:px-6 pt-3 border-b border-slate-100 overflow-x-auto">
            {[
              { key: 'details', label: 'Details', icon: Tag },
              { key: 'comments', label: `Discussion (${comments?.length || 0})`, icon: MessageSquare },
              { key: 'history', label: 'History', icon: History },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {tab === 'details' && (
              <div className="space-y-4">
                {task.description && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400 mb-1">Description</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{task.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Start:</span>
                    <span className="text-slate-700">{task.start_date || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Due:</span>
                    <span className="text-slate-700">{task.due_date || '-'}</span>
                  </div>
                  {task.module && (
                    <div className="flex items-center gap-2 text-sm">
                      <Tag className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-500">Module:</span>
                      <span className="text-slate-700">{task.module}</span>
                    </div>
                  )}
                  {task.business && (
                    <div className="flex items-center gap-2 text-sm">
                      <Tag className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-500">Business:</span>
                      <span className="text-slate-700">{task.business}</span>
                    </div>
                  )}
                </div>
                {task.assignees?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400 mb-2 flex items-center gap-1"><Users className="h-3 w-3" /> Assignees</p>
                    <div className="flex flex-wrap gap-2">
                      {task.assignees.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
                          <span className="w-5 h-5 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-[9px] font-bold">{getInitials(a.name)}</span>
                          {a.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {task.labels?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Labels</p>
                    <div className="flex flex-wrap gap-1.5">
                      {task.labels.map((l, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{l}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {tab === 'comments' && (
              <div className="space-y-3">
                {(!comments || comments.length === 0) && <p className="text-sm text-slate-400 text-center py-8">No comments yet.</p>}
                {comments?.map(c => (
                  <div key={c.id} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#002FA7]/10 flex-shrink-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-[#002FA7]">{getInitials(c.user?.name)}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-slate-800">{c.user?.name}</span>
                        <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-700">{c.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === 'history' && (
              <div className="space-y-2">
                {(!history || history.length === 0) && <p className="text-sm text-slate-400 text-center py-8">No history.</p>}
                {history?.map(h => (
                  <div key={h.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                    <Clock className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-slate-700"><strong>{h.user_name}</strong> {h.action}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{new Date(h.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Shared from Intelvolks Tasks</p>
      </div>
    </div>
  );
}
